import React from 'react';

import { TOOL_IDS } from '@adsk/alloy-provider';
import { EnvelopeIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { isInExoSkeleton } from '@adsk/exo-skeleton-api';
import { RemoteComponent } from '@adsk/exo-skeleton-react';

import {
  CorrespondenceEntityBlock,
  CorrespondenceEntityMenuItem,
} from './components';
import CorrNonExoProvider from './components/NonExoCorrProvider';
import { getEntitiesByIds } from './helpers';
import { Thread } from './types';
import ReferencePickerPlugin from '../../types/ReferencePickerPlugin';

const PluralText = ({ lowerCase }: Record<string, unknown>) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const result = T.translate('CORRESPONDENCE');
  if (lowerCase && typeof result === 'string') {
    return result.toLowerCase();
  }
  return result;
};

const RemoteCustomFilterButton = (props: Record<string, unknown>) => {
  return (
    <RemoteComponent
      scope="accCorrespondence"
      moduleName="./ReferencePickerPlugin/CustomSearchFilterButton"
      renderLoader={() => null}
      {...props}
    />
  );
};

const RemoteCustomDataView: ReferencePickerPlugin<Thread>['CustomDataView'] = (
  props,
) => {
  return (
    <RemoteComponent
      scope="accCorrespondence"
      moduleName="./ReferencePickerPlugin/ThreadsTable"
      renderLoader={() => null}
      {...props}
    />
  );
};

const RemoteFilterPanel: ReferencePickerPlugin<Thread>['FiltersPanel'] = (
  props,
) => {
  return (
    <RemoteComponent
      scope="accCorrespondence"
      moduleName="./ReferencePickerPlugin/FiltersPanel"
      renderLoader={() => null}
      {...props}
    />
  );
};

const RemoteProvider: ReferencePickerPlugin<Thread>['Provider'] = ({
  ...props
}) => {
  // Mock Provider for non-ExoSkeleton environments (otherwise ReferencePicker will not render at all)
  if (!isInExoSkeleton()) {
    // @ts-expect-error - Not implementing all params
    return <CorrNonExoProvider {...props} />;
  }

  return (
    <RemoteComponent
      scope="accCorrespondence"
      moduleName="./ReferencePickerPlugin/RefPickerProvider"
      renderLoader={() => null}
      {...props}
    />
  );
};

const RemoteCreateNewEntityPanel: ReferencePickerPlugin<Thread>['CreateNewEntityPanel'] =
  (props) => {
    return (
      <RemoteComponent
        scope="accCorrespondence"
        moduleName="./ReferencePickerPlugin/CreateNewEntityPanel"
        renderLoader={() => null}
        {...props}
      />
    );
  };

// see libs/reference-picker/types/ReferencePickerPlugin.ts for details on plugin props
const plugin: ReferencePickerPlugin<Thread> = {
  metadata: {
    domain: 'autodesk-construction-correspondence',
    type: 'thread',
  },
  is3LOSupported: true,
  toolId: TOOL_IDS.CORRESPONDENCE,
  renderIcon: (p) => <EnvelopeIcon {...p} />,
  PluralText,
  getEntitiesByIds,
  EntityBlock: CorrespondenceEntityBlock,
  EntityMenuItem: CorrespondenceEntityMenuItem,

  /*  Disable queryEntities (which means disabling Adding Corr Ref in the Ref Picker)
      outside of ExoSkeleton as long as Remote Component isn't supported outside of ExoSkeleton */
  //@ts-expect-error - Not implementing all params
  queryEntities:
    isInExoSkeleton() && (async () => ({ data: [{ id: '__PLACEHOLDER__' }] })),

  // Remote Components
  Provider: RemoteProvider,
  CustomDataView: RemoteCustomDataView,
  FiltersPanel: RemoteFilterPanel,
  CreateNewEntityPanel: RemoteCreateNewEntityPanel,
  canCreate: () => Promise.resolve(isInExoSkeleton()),

  // Search & Filter Buttons
  // showSearchBox: true,
  // CustomPickerSearchBox: () => null,
  CustomFilterButton: RemoteCustomFilterButton,

  AddRelationshipsButtonText: ({ count }: { count: number }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate('ADD_CORRESPONDENCES', { context: count });
  },
  SelectedCount: ({ count }: { count: number }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return count
      ? T.translate('CORRESPONDENCES_SELECTED', { context: count })
      : null;
  },
  PickerModalTitle: () => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate('ADD_CORRESPONDENCE_REFERENCE');
  },
};

export default plugin;
